package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.DecideProposalsRequest
import dev.lingvo.shared.MAX_PROPOSAL_DECISION_RESULTS
import dev.lingvo.shared.ProposalDecisionOutcome
import dev.lingvo.shared.ProposalDecisionReceiptDto
import dev.lingvo.shared.ProposalDto
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.shared.validateLocale

internal data class ProposalsAcceptedDraft(val proposalId: String, val keyId: String, val locale: String, val keyName: String?)

internal data class ProposalsReceiptView(
    val decided: Int,
    val skipped: Int,
    val truncated: Boolean,
    val acknowledged: Set<String>,
    val accepted: List<ProposalsAcceptedDraft>,
)

/** An admitted receipt records the completed operation, never a future approval/content promise. */
internal fun validatedProposalsReceipt(
    receipt: ProposalDecisionReceiptDto,
    slug: String,
    request: DecideProposalsRequest,
    known: List<ProposalDto>,
): ProposalsReceiptView? {
    val results = receipt.results ?: return null
    val runId = request.runId
    if (receipt.projectSlug != slug || receipt.runId != runId || receipt.decided < 0 ||
        results.size > MAX_PROPOSAL_DECISION_RESULTS
    ) return null
    val explicitIds = request.ids.toSet()
    if (runId != null) {
        if (!isCanonicalSourceReviewId(runId) || known.none { it.runId == runId }) return null
    } else if (explicitIds.isEmpty() || explicitIds.any { !isCanonicalSourceReviewId(it) }) return null
    val ids = results.map { it.proposalId }
    if (ids.any { !isCanonicalSourceReviewId(it) } || ids.distinct().size != ids.size) return null
    if (runId == null && (ids.any { it !in explicitIds } || receipt.decided > explicitIds.size ||
            !receipt.resultsTruncated && ids.toSet() != explicitIds ||
            receipt.resultsTruncated && explicitIds.size <= MAX_PROPOSAL_DECISION_RESULTS)) return null
    val identifiedDecisions = results.count { it.outcome != ProposalDecisionOutcome.SKIPPED }
    if (receipt.decided < identifiedDecisions || !receipt.resultsTruncated && receipt.decided != identifiedDecisions) return null
    val knownById = known.associateBy { it.id }
    for (result in results) {
        if (result.outcome == ProposalDecisionOutcome.SKIPPED) {
            if (result.keyId != null || result.locale != null || result.targetRevision != null) return null
            continue
        }
        if (request.accept != (result.outcome == ProposalDecisionOutcome.ACCEPTED)) return null
        val keyId = result.keyId ?: return null
        val locale = result.locale ?: return null
        if (!isCanonicalSourceReviewId(keyId) || validateLocale(locale) != null) return null
        if (result.outcome == ProposalDecisionOutcome.ACCEPTED) {
            if (result.targetRevision?.let(::isCanonicalSourceReviewId) != true) return null
        } else if (result.targetRevision != null) return null
        knownById[result.proposalId]?.let { proposal ->
            if (proposal.keyId != keyId || proposal.locale != locale ||
                runId != null && proposal.runId != runId) return null
        }
    }
    return ProposalsReceiptView(
        receipt.decided,
        results.count { it.outcome == ProposalDecisionOutcome.SKIPPED },
        receipt.resultsTruncated,
        results.filter { it.outcome != ProposalDecisionOutcome.SKIPPED }.map { it.proposalId }.toSet(),
        results.filter { it.outcome == ProposalDecisionOutcome.ACCEPTED }.map {
            ProposalsAcceptedDraft(it.proposalId, it.keyId!!, it.locale!!, knownById[it.proposalId]?.keyName)
        },
    )
}

/** Actor/project/role-keyed by the caller. Only a read admitted after uncertainty can reopen writes. */
@Stable
internal class ProposalsDecisionState {
    var busy by mutableStateOf(false)
    var failure by mutableStateOf<String?>(null)
        private set
    var receipt by mutableStateOf<ProposalsReceiptView?>(null)
        private set
    var accepted by mutableStateOf<List<ProposalsAcceptedDraft>>(emptyList())
        private set
    var acknowledged by mutableStateOf<Set<String>>(emptySet())
        private set
    var unavailable by mutableStateOf(false)
        private set
    var readRequired by mutableStateOf(false)
        private set
    private var epoch = 0L

    fun readAdmission(): Long = epoch

    fun readCompleted(admittedEpoch: Long) {
        if (admittedEpoch != epoch) return
        readRequired = false
        failure = null
    }

    fun confirmed(value: ProposalsReceiptView) {
        epoch++
        receipt = value
        failure = null
        unavailable = false
        acknowledged = acknowledged + value.acknowledged
        accepted = (accepted + value.accepted).distinctBy { it.proposalId }
        readRequired = value.truncated
    }

    fun unconfirmed(message: String? = null) {
        epoch++
        failure = message
        unavailable = true
        readRequired = true
    }
}
